import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '@/api/client';
import { MapPin, Loader2, Search } from 'lucide-react';
import { scrollFocusedFieldIntoView } from '@/lib/scrollIntoViewOnFocus';

// The district shown under a cafe name, ordered from the most specific
// component that reads as a place name down to the broadest fallback.
//
// The generic 'sublocality' and 'political' types are deliberately absent. In a
// Japanese address every chome / banchi number carries 'sublocality', and
// almost every component — country and prefecture included — carries
// 'political', so either one matches a bare number long before it matches a
// district. That is how a cafe in Tsukiji came to be filed under "12": its
// address carried no 'sublocality_level_1', the list fell through to the
// generic 'sublocality', and the most specific numeric component won. Bangkok
// never showed it because a khwaeng is a 'sublocality_level_1' and matched on
// the first pass.
const AREA_TYPES = [
  'sublocality_level_1',
  'sublocality_level_2',
  'neighborhood',
  'locality',
  'postal_town',
  'administrative_area_level_2',
];

const CITY_TYPES = ['administrative_area_level_1', 'locality', 'postal_town'];

// A component with no letters, or one opening with a digit, is a block or
// chome number rather than the name of a place.
function looksLikePlaceName(value) {
  return /\p{L}/u.test(value ?? '') && !/^\d/.test(value ?? '');
}

// The first component carrying one of `types`, in the order `types` lists them
// — so the priority is the caller's, not Google's ordering of the address.
function pickComponent(components, types) {
  for (const type of types) {
    const match = components?.find(
      (c) => c.types?.includes(type) && looksLikePlaceName(c.long_name),
    );
    if (match) return match.long_name;
  }
  return '';
}

export default function PlaceAutocomplete({ onSelect, placeholder = 'Search for a cafe...' }) {
  const [value, setValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const search = useCallback(async (input) => {
    if (!input || input.length < 2) { setPredictions([]); return; }
    setLoading(true);
    const res = await api.functions.invoke('searchPlaces', { action: 'autocomplete', input });
    setPredictions(res.data?.predictions || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 400);
    return () => clearTimeout(debounceRef.current);
  }, [value, search]);

  useEffect(() => {
    const handleClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = async (prediction) => {
    setValue(prediction.structured_formatting?.main_text || prediction.description);
    setShowDropdown(false);
    setPredictions([]);

    const res = await api.functions.invoke('searchPlaces', { action: 'details', place_id: prediction.place_id });
    const place = res.data?.result;
    if (!place) return;

    const area = pickComponent(place.address_components, AREA_TYPES);

    let city = pickComponent(place.address_components, CITY_TYPES);
    // Normalize Bangkok variants to consistent English name
    if (['กรุงเทพมหานคร', 'Krung Thep Maha Nakhon', 'Krung Thep'].includes(city)) {
      city = 'Bangkok';
    }

    onSelect({
      place_id: place.place_id,
      name: place.name || '',
      area,
      city,
      formatted_address: place.formatted_address || '',
    });
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
      </div>
      <input
        value={value}
        onChange={(e) => { setValue(e.target.value); setShowDropdown(true); }}
        onFocus={(e) => { if (value.length >= 2) setShowDropdown(true); scrollFocusedFieldIntoView(e); }}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-3 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
      />
      {showDropdown && predictions.length > 0 && (
        <ul className="absolute z-50 top-full mt-1 w-full bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          {predictions.map((p) => (
            <li
              key={p.place_id}
              onMouseDown={() => handleSelect(p)}
              className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-secondary transition-colors"
            >
              <MapPin size={15} className="text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">{p.structured_formatting?.main_text || p.description}</p>
                {p.structured_formatting?.secondary_text && (
                  <p className="text-xs text-muted-foreground">{p.structured_formatting.secondary_text}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}